import {
    CONSORS_LOAN_OFFERS_REQUEST_FAILURE_REASONS,
    LOAN_REQUEST_OFFER_SOURCES,
    LOAN_REQUEST_OFFER_STATUS,
    LOAN_REQUEST_USAGES,
    OFFER_ACCEPTANCE_STATUSES,
    OFFER_DOCUMENT_UPLOAD_STATUSES,
} from 'cpool-constants';
import { LoanRequest_EuropaceOfferAcceptanceResponseSuccessReceived, LoanRequest_OfferRecorded } from 'cpool-events';
import { schemaBigDecimal, schemaConsorsGetSubscription, schemaEuropaceCommission, schemaId } from 'cpool-schemas';
import { z } from 'zod';

export const schemaAcceptedOffersCommon = z.object({
    acceptanceStatus: z.nativeEnum(OFFER_ACCEPTANCE_STATUSES),
    // timestamp when the offer was accepted
    acceptedAt: z.number().nullable(),
    // Europace "antrag" id
    applicationId: z.string().nullish(),
    commissionResponseInPercent: z.optional(schemaBigDecimal),
    // nullish: Consors rows may omit these until financialFramework exists (or if rejected with no framework)
    creditAmount: z.number().nullish(),
    // this is the disbursement amount (Auszahlungsbetrag)
    disbursementAmount: z.number().nullish(),
    favored: z.boolean().default(false),
    id: schemaId,
    // this is the effective nominal interest rate (Effektivzins)
    interest: z.number().nullish(),
    // loan-request id in our system
    loanRequestId: schemaId,
    // this is the nominal interest rate (Sollzins)
    nominalInterest: z.number().nullish(),
    providerApplicationNumber: z.string().nullish(),
    provision: z.number().nullish(),
    rate: z.number().nullish(),
    runtime: z.number().nullish(),
    // timestamp of the moment the offer was sent; can be corrected manually for offers sent outside of C-Pool
    sentAt: z.number().nullable().default(null),
    status: z.nativeEnum(LOAN_REQUEST_OFFER_STATUS).nullable(),
    teaser: z.boolean().default(false),
    // this is the total credit amount (Gesamtkreditbetrag)
    totalCreditAmount: z.number().nullish(),
});

export const schemaAcceptedOffersEuropace = z
    .object({
        applicant1ResidualDebtInsurance: LoanRequest_EuropaceOfferAcceptanceResponseSuccessReceived.prototype
            .getSchema()
            .shape.acceptedOffer.shape.antrag.unwrap()
            .shape.ratenschutz.unwrap()
            .shape.versicherteRisikenAntragsteller1.nullable(),
        applicant2ResidualDebtInsurance: LoanRequest_EuropaceOfferAcceptanceResponseSuccessReceived.prototype
            .getSchema()
            .shape.acceptedOffer.shape.antrag.unwrap()
            .shape.ratenschutz.unwrap()
            .shape.versicherteRisikenAntragsteller2.nullable(),
        // this is the name of the bank which can be used as an alt in the frontend
        bank: z.string().nullable(),
        // this logo is an url to a svg
        bankLogo: z.string().nullable(),
        commission: schemaEuropaceCommission.optional(),
        documents: z
            .array(
                LoanRequest_EuropaceOfferAcceptanceResponseSuccessReceived.prototype
                    .getSchema()
                    .shape.acceptedOffer.shape.antrag.unwrap()
                    .shape.dokumente.unwrap()
                    .element.merge(
                        z.object({
                            documentKey: z.string().nullable(),
                            uploadStatus: z.nativeEnum(OFFER_DOCUMENT_UPLOAD_STATUSES),
                        }),
                    ),
            )
            .nullable(),
        // offerId from Europace is not an uuid
        offerId: z.string(),
        // id for the offer request
        offerRequestId: schemaId,
        productDescription: z.string().nullable(),
        source: z.literal(LOAN_REQUEST_OFFER_SOURCES.EUROPACE),
    })
    .merge(schemaAcceptedOffersCommon);

export const schemaAcceptedOffersEuropaceSync = schemaAcceptedOffersEuropace
    .omit({
        offerId: true,
        offerRequestId: true,
        source: true,
    })
    .merge(z.object({ source: z.literal(LOAN_REQUEST_OFFER_SOURCES.EUROPACE_SYNC) }));

export const schemaAcceptedOffersConsors = z
    .object({
        applicant1ResidualDebtInsurance: LoanRequest_OfferRecorded.prototype
            .getSchema()
            .shape.applicant1ResidualDebtInsurance.nullish(),
        applicant2ResidualDebtInsurance: LoanRequest_OfferRecorded.prototype
            .getSchema()
            .shape.applicant2ResidualDebtInsurance.nullish(),
        bank: z.literal('Consors Finanz').nullable(),
        bankLogo: z.string().nullable(),
        errorMessage: z.string().nullable(),
        failureReason: z.nativeEnum(CONSORS_LOAN_OFFERS_REQUEST_FAILURE_REASONS).nullable(),
        offer: schemaConsorsGetSubscription.shape.subscriptionList.unwrap().element.shape.financialFramework.nullish(),
        source: z.literal(LOAN_REQUEST_OFFER_SOURCES.CONSORS),
        subscriptionIdentifier: z.string().nullish(),
        supportingDocumentsRequired: z.array(z.number()).optional(),
    })
    .merge(schemaAcceptedOffersCommon);

export const schemaAcceptedOffersManual = z
    .object({
        applicant1ResidualDebtInsurance: LoanRequest_OfferRecorded.prototype
            .getSchema()
            .shape.applicant1ResidualDebtInsurance.nullable(),
        applicant2ResidualDebtInsurance: LoanRequest_OfferRecorded.prototype
            .getSchema()
            .shape.applicant2ResidualDebtInsurance.nullable(),
        // nullable: manual offers recorded before this field existed have no value for it
        applicantAttribution: LoanRequest_OfferRecorded.prototype
            .getSchema()
            .shape.applicantAttribution.nullable()
            .default(null),
        bank: LoanRequest_OfferRecorded.prototype.getSchema().shape.bank,
        bankLogo: z.null(),
        createdAt: z.coerce.date(),
        documents: LoanRequest_OfferRecorded.prototype.getSchema().shape.file.nullable(),
        financingPurpose: z.nativeEnum(LOAN_REQUEST_USAGES).nullish(),
        offerId: z.null(),
        offerRequestId: z.null(),
        source: z.literal(LOAN_REQUEST_OFFER_SOURCES.MANUAL),
    })
    .merge(schemaAcceptedOffersCommon);
